import React, { useRef } from 'react';

import { sectionsMessages } from '../../../../../translates/sectionsMessages';
import { dismissSequencedFlow } from '../../../../actions/contentItemActions';
import { ContentWrapper } from '../../../../components/ContentWrapper/ContentWrapper';
import { ContentSizeSwitcher } from '../../../../components/shared/ContentSizeSwitcher';
import { Fade } from '../../../../components/transitions/Fade';
import { ContentBlocksRenderer } from '../../../../ContentZone/student/ContentBlocksRenderer';
import { useIntlReexport } from '../../../../hooks/useIntlReexport';
import { SwitchType } from '../../../../hooks/useSwitch';
import { useStudentSelector } from '../../../../store/selectors/useStudentSelector';
import { useSequencedContentFlowContext } from '../../context/SequencedContentFlowContext';
import { ContentRenderer } from '../ContentRenderer';
import { SequencedFlowProgressBar } from './SequencedFlowProgressBar';
import { isSection, isSequencedFlowStep, ModalSize, Section, SequencedFlowChildrenTypes } from '@ael/core-api';
import { useDispatch } from 'react-redux';
import { FroalaEditorsSettings } from 'src/config/configs';

type SequencedFlowContentProps = {
  currentContentItem: SequencedFlowChildrenTypes;
  isContentCollapsed: boolean;
  panelVisibility: SwitchType;
  onGoToSection: (section: Section) => void;
  froalaEditorsSettings: FroalaEditorsSettings;
};

export const SequencedFlowContent: React.FC<SequencedFlowContentProps> = (props) => {
  const { currentContentItem, isContentCollapsed, panelVisibility, onGoToSection, froalaEditorsSettings } = props;
  const ref = useRef(null);
  const dispatch = useDispatch();
  const { formatMessage } = useIntlReexport();
  const isStepDisplayed = useStudentSelector((state) => state.currentSequencedFlowPosition.displayed);
  const { currentSection } = useSequencedContentFlowContext();
  const isSmallModalSize = isSection(currentContentItem) || currentContentItem.modalSize === ModalSize.Small;

  const isStepAndDismissable =
    isStepDisplayed &&
    isSequencedFlowStep(currentContentItem) &&
    Boolean(currentContentItem.position) &&
    !isSmallModalSize;

  const onDismissSequencedContent = () => dispatch(dismissSequencedFlow());

  return (
    <Fade ref={ref}>
      <ContentSizeSwitcher
        ref={ref}
        isSmallModal={isSmallModalSize}
        panelVisibility={panelVisibility}
        key={currentContentItem.id}
      >
        <ContentWrapper.Modal collapsed={isContentCollapsed}>
          <ContentWrapper.TopBar positionFixed>
            <SequencedFlowProgressBar currentContentItemId={currentContentItem.id} />
          </ContentWrapper.TopBar>
          {!isContentCollapsed && (
            <ContentWrapper.DesktopContent>
              <ContentWrapper.Title
                title={
                  isSection(currentContentItem) ? formatMessage(sectionsMessages.choose) : currentContentItem.title
                }
                onClose={isStepAndDismissable ? onDismissSequencedContent : undefined}
                medium={isSmallModalSize}
              />
              <ContentWrapper.Body noHorizontalPadding>
                {isSection(currentContentItem) ? (
                  <ContentRenderer
                    currentContentItem={currentContentItem}
                    currentSectionItem={currentSection}
                    onGoToSection={onGoToSection}
                    froalaEditorsSettings={froalaEditorsSettings}
                  />
                ) : (
                  <ContentBlocksRenderer
                    contentItem={currentContentItem}
                    content={currentContentItem.content}
                    inSmallWindow={isSmallModalSize}
                    froalaEditorsSettings={froalaEditorsSettings}
                  />
                )}
              </ContentWrapper.Body>
            </ContentWrapper.DesktopContent>
          )}
        </ContentWrapper.Modal>
      </ContentSizeSwitcher>
    </Fade>
  );
};
